import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { TextInput } from '@/components/form/fields/text/TextInput';
import { Loader } from '@/components/loading/Loading';
import { useModal } from '@/components/modal/useModal';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/tabs/Tabs';
import SiSearch from '@/icons/SiSearch';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { PageLoading } from '@/layouts/PageLoading';
import { Container } from '@/layouts/main/Main';

import { DaasTableColumn } from '@/components/table/DaasTable';
import { MappedCustomerCreditCheck } from '@/services/api/hooks/credit';
import { PageTitle } from '@/utils/Title';
import { useSearch } from '@/utils/search';
import { FunctionComponent, Suspense, useState } from 'react';
import CreditCheckTable, { CreditCheckTableData } from '../CreditCheckTable';
import MspEditCreditCheckModal from './MspEditCreditCheckModal';
import { useBuildMspCreditChecks } from './useBuildMspCreditChecks';

interface TabContentProps {
  data: MappedCustomerCreditCheck[];
}

const tableHeaders: DaasTableColumn[] = [
  {
    accessor: 'name',
    sortable: true,
    text: 'MSP Name'
  },
  {
    accessor: 'number',
    sortable: true,
    text: 'Company Number'
  },
  {
    accessor: 'creditLimit',
    sortable: true,
    text: 'Credit Limit',
    size: '0.7fr'
  },
  {
    accessor: 'createdAt',
    sortable: true,
    text: 'Created At'
  },
  {
    accessor: 'lastModified',
    sortable: true,
    text: 'Last Modified'
  },
  {
    accessor: 'userId',
    sortable: true,
    text: 'Modified By'
  },
  {
    accessor: '',
    sortable: false,
    text: '',
    size: '0.4fr'
  }
];

const TabContent: FunctionComponent<TabContentProps> = ({ data }) => {
  const modalProps = useModal<{ mspId: string; creditCheckId?: string }>();

  return (
    <Suspense
      fallback={
        <div className="w-full py-8 flex justify-center items-center">
          <Loader className="h-8 w-8" />
        </div>
      }
    >
      {data.length > 0 ? (
        <>
          <MspEditCreditCheckModal {...modalProps} />
          <CreditCheckTable
            onEdit={(_regionCode: string, mspId: string, creditCheckId?: string) =>
              modalProps.open({ mspId, creditCheckId })
            }
            data={data as CreditCheckTableData[]}
            headers={tableHeaders}
          />
        </>
      ) : (
        <p className="font-semibold text-lg mt-10">No credit checks available</p>
      )}
    </Suspense>
  );
};

const CreditCheckContent: FunctionComponent = () => {
  const breadcrumbs: NavPage[] = [
    {
      name: 'Credit Check',
      to: ''
    }
  ];

  const data = useBuildMspCreditChecks();

  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredCreditChecksByName = useSearch(data, ['name'], searchQuery);

  const reviewItems = filteredCreditChecksByName.filter(item => item.creditLimit === undefined);

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Credit Check - MSPs</h3>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto">
        <Container animate="pop" className="w-full py-8">
          <Tabs defaultValue={`${reviewItems.length ? 'reviewNeeded' : 'all'}`}>
            <div className="space-y-10">
              <div>
                <TabsList>
                  <TabsTrigger
                    className="px-4 text-2xl border-black data-[state=active]:border-b-[3px] data-[state=active]:text-black data-[state=active]:font-semibold"
                    value="all"
                  >
                    All MSPs ({data.length})
                  </TabsTrigger>
                  <TabsTrigger
                    className="px-4 text-2xl border-black data-[state=active]:border-b-[3px] data-[state=active]:text-black data-[state=active]:font-semibold"
                    value="reviewNeeded"
                  >
                    Review Needed ({reviewItems.length})
                  </TabsTrigger>
                </TabsList>
              </div>
              <div className="space-y-3">
                <div className="flex space-x-5">
                  <TextInput
                    id="filter"
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder={'Search for msps'}
                    prefix={<SiSearch className="text-xl" />}
                  />
                </div>
                <TabsContent value="all">
                  <TabContent data={filteredCreditChecksByName} />
                </TabsContent>
                <TabsContent value="reviewNeeded">
                  <TabContent data={reviewItems} />
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </Container>
      </div>
    </>
  );
};

const MspCreditCheckPage: FunctionComponent = () => {
  return (
    <>
      <PageTitle>Credit Check</PageTitle>
      <Suspense fallback={<PageLoading />}>
        <CreditCheckContent />
      </Suspense>
    </>
  );
};

export default MspCreditCheckPage;
